import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Avatar, Button, Space} from 'antd';
import {
  StyledHistoryDetails,
  StyledHistoryDetailsButtonContainer,
  StyledHistoryDetailsMessage,
  StyledHistoryDetailsMessageFieldValue
} from './StyledHistoryDetails';
import History from '../../../../../History';
import {subscriptionActions} from '../../../../../../state/actions/subscription.actions';
import {
  amountHelpers,
  dateHelpers,
  objectHelpers,
  subscriptionsHelpers,
  textHelpers
} from '../../../../../../utils/helpers';

const {getObjProp: gObjProp} = objectHelpers;

const perPagePagination = 10;

const defaultQuery = {
  order_by: 'created_at',
  per_page: perPagePagination,
}

const excludedHistoryFields = ['attribute_values', 'is_card_used'];

const HistoryDetails = ({
  dispatch,
  getSubscriptionLogs,
  subscription,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'subscriptions']);
  const [pagination, setPagination] = useState({next_page: null, cursor: null});
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);

  const trans = (key, options) => t(`subscriptions:${key}`, options);

  const getDate = (timestamp) => dateHelpers.getTimestampDate(timestamp, 'DD MMM YYYY');

  const getFieldValue = (details, key) => {
    let value = details[key];
    const fields = {
      'budget_limit': () => amountHelpers.getAmountWithCurrencyCode(value),
      'billed': () => subscriptionsHelpers.getBillType(value),
      'category': () => {
        const category = subscriptionsHelpers.getSubscriptionCategory(value);
        return category && category.key ? t(category.key) : value;
      },
      'card_limit_period': () => subscriptionsHelpers.getCardLimitType(value),
      'card_owner': () => objectHelpers.getObjProp(value, 'full_name'),
      'contract_file': () => {
        const fileName = textHelpers.getFilenameFromUrl(value);
        return fileName ? <a href={value} rel='noreferrer' target='_blank'>{fileName}</a> : fileName;
      },
      'contract_renewal_date': () => getDate(value),
      'contract_renewal_period': () => subscriptionsHelpers.getContractRenewalPeriodType(value),
      'contract_renewal_reminder': () => getDate(value),
      'custom_limit': () => amountHelpers.getAmountWithCurrencyCode(value),
      'duration': () => subscriptionsHelpers.getDurationType(value),
      'expected_first_payment': () => subscriptionsHelpers.getSubscriptionDate(details, key),
      'owner': () => objectHelpers.getObjProp(value, 'full_name'),
      'payment_frequency': () => subscriptionsHelpers.getPaymentFrequencyType(value),
      'payment_type': () => subscriptionsHelpers.getPaymentType(value),
      'target_status': () => subscriptionsHelpers.getStatusType(value),
      'status': () => subscriptionsHelpers.getStatusType(value)
    }
    if (fields.hasOwnProperty(key)) {
      value = fields[key]();
      if (typeof value === 'string') value = t(value);
    }
    if (Array.isArray(value)) value = value.length === 0 ? '' : typeof value === 'boolean' ? value : value.join(', ');

    return value;
  }

  const getHistoryItems = (data) => {
    return data.map(d => {
      const {details} = d;
      const reporter = gObjProp(d, 'reported_by');
      const reporterName = gObjProp(reporter, 'full_name');

      const fieldsData = Object.keys(details)
        .filter(field => !excludedHistoryFields.includes(field))
        .map(field => ({ name: field, value: getFieldValue(details, field) }))
        .filter(field => field.value);

      return fieldsData.length ? {
        key: d.id,
        date: dateHelpers.getTimestampDate(d.created_at, 'DD/MM/YY'),
        message: (
          <StyledHistoryDetailsMessage size='middle' align='start'>
            <Avatar src={reporter?.logo}>
              {textHelpers.getInitials(reporterName)}
            </Avatar>
            <div className='message-details'>
              <div><span className='fw-500'>{reporterName}</span> {t('changedTheseValues')}:</div>
              <Space direction='vertical' size={2}>
                {fieldsData.map(({name, value}, index) => {
                  if (value !== undefined) {
                    let displayedValue = value !== '' && value !== null ? value : t('setToEmpty');
                    return (
                      <span key={`field-${name}-${index}`}>
                        <span>
                          {trans(`subscriptionFields.${name}`)}: <StyledHistoryDetailsMessageFieldValue>{displayedValue}</StyledHistoryDetailsMessageFieldValue>
                        </span>
                      </span>
                    )
                  } else {
                    return ''
                  }
                })}
              </Space>
            </div>
          </StyledHistoryDetailsMessage>
        )
      } : null
    }).filter(value => value); // exclude message with no changed fields
  }

  useEffect(() => {
    const subscriptionId = gObjProp(subscription, 'id');
    if (subscriptionId) {
      getSubscriptionLogs(
        subscriptionId,
        defaultQuery,
        ({results, pagination}) => {
          setHistoryData(getHistoryItems(results));
          setPagination(pagination);
        }
      )
    }
  }, [subscription]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShowMore = () => {
    if (loading) return;

    const subscriptionId = gObjProp(subscription, 'id');
    if (subscriptionId) {
      setLoading(true);
      getSubscriptionLogs(
        subscriptionId,
        {
          ...defaultQuery,
          cursor: pagination.cursor
        },
        ({results, pagination}) => {
          setHistoryData([
            ...historyData,
            ...getHistoryItems(results)
          ]);
          setPagination(pagination);
          setLoading(false);
        },
        () => setLoading(false)
      )
    }
  }

  const isEnableLoadMore = pagination.next_page;

  return (
    <StyledHistoryDetails
      direction='vertical'
      align='center'
      size='large'
      {...rest}
    >
      <History data={historyData} />
      <StyledHistoryDetailsButtonContainer>
        {isEnableLoadMore && (
          <Button
            loading={loading}
            onClick={handleShowMore}
            size='large'
            type='default'
          >
            {t('showMore')}
          </Button>
        )}
      </StyledHistoryDetailsButtonContainer>
    </StyledHistoryDetails>
  );
}

HistoryDetails.propTypes = {
  subscription: PropTypes.shape({
    id: PropTypes.string,
  }),
}

const mapDispatchToProps = {
  getSubscriptionLogs: subscriptionActions.getSubscriptionLogs
}

export default connect(null, mapDispatchToProps)(HistoryDetails);


